import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import i18n from '../i18n';
import { get, post, put, del } from '../utils/api';
import { useSuccessSnack, useErrorSnack } from '../utils/snackbar';
import { UPLOADING_COUNTER } from './FileActions';

async function createFolder({
  name,
  folder_prefix,
  parentId,
  organizationId,
  orgFullAccess,
  isCase,
  caseTemplateId,
}) {
  const res = await post('/folders', {
    name,
    folder_prefix,
    parent_id: parentId,
    organization_id: organizationId,
    org_full_access: orgFullAccess,
    is_case: isCase,
    case_template_id: caseTemplateId,
  });
  return res.data;
}

export async function getFolders(prefix, { search = '', limit = 10 } = {}) {
  if (!prefix || prefix === '/') {
    return {};
  }

  const res = await get(`/folders${prefix ? `${prefix}` : ''}`, {
    params: { search, limit },
  });
  return res.data;
}

async function getOrganizationFolders(
  prefix,
  organizationId,
  { onlyEditAccess, type, startDate, endDate, query, types } = {}
) {
  // onlyEditAccess - folders where user has edit access. (i.e role is edit or admin)
  if (prefix === null) {
    return {};
  }

  const res = await get(
    `/organization/${organizationId}/folders${prefix ? `${prefix}` : ''}`,
    {
      params: {
        only_edit_access: +!!onlyEditAccess,
        types: (types || []).join(','),
        type,
        startDate,
        endDate,
        query,
      },
    }
  );
  return res.data;
}

async function updateFolder(folderId, { name, folder_prefix }) {
  const res = await put(`/folders/${folderId}`, {
    name,
    folder_prefix,
  });
  return res.data;
}

async function deleteFolder(folderId) {
  const res = await del(`/folders/${folderId}`);
  return res.data;
}

export function useFolderActions(folderId) {
  const queryClient = useQueryClient();

  const { mutate: createFolderMutate, mutateAsync: createFolderAsync } =
    useMutation(createFolder, {
      onSuccess: () => {
        queryClient.invalidateQueries('folders');
        queryClient.invalidateQueries('folderOrganization');
      },
    });

  const {
    mutate: updateFolderMutate,
    mutateAsync: updateFolderAsync,
    isSuccess: isUpdateSuccess,
    isError: isUpdateError,
  } = useMutation((data) => updateFolder(folderId, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('folders');
      queryClient.invalidateQueries('folderOrganization');
    },
  });

  useSuccessSnack(i18n.t('Mapp uppdaterad'), isUpdateSuccess);
  useErrorSnack(i18n.t('Kunde inte uppdatera mapp'), isUpdateError);

  const {
    mutate: deleteFolderMutate,
    mutateAsync: deleteFolderAsync,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
  } = useMutation((data) => deleteFolder(folderId), {
    onSuccess: () => {
      queryClient.invalidateQueries('folders');
      queryClient.invalidateQueries('folderOrganization');
    },
  });

  useSuccessSnack(i18n.t('Mapp borttagen'), isDeleteSuccess);
  useErrorSnack(i18n.t('Kunde inte ta bort mapp'), isDeleteError);

  return {
    createFolder: createFolderMutate,
    createFolderAsync,
    updateFolder: updateFolderMutate,
    updateFolderAsync,
    deleteFolder: deleteFolderMutate,
    deleteFolderAsync,
  };
}

export function useFoldersQuery(prefix) {
  const { data, isLoading, error } = useQuery(['folders', prefix], () =>
    getFolders(prefix)
  );

  const { folders, currentFolder } = useMemo(() => {
    const folders = data?.folders || [];
    const currentFolder = data?.current || null;
    return {
      folders,
      currentFolder,
    };
  }, [data]);

  return {
    folders,
    currentFolder,
    isLoading,
    error,
  };
}

export function useFolderOrganizationQuery({
  folderPrefix,
  organizationId,
  type,
  types,
  startDate,
  endDate,
  query,
  onlyEditAccess = false,
}) {
  const { data, isLoading, error, isFetching } = useQuery(
    [
      'folderOrganization',
      folderPrefix,
      organizationId,
      onlyEditAccess,
      type,
      (types || []).join(','),
      startDate,
      endDate,
      query,
    ],
    () =>
      getOrganizationFolders(folderPrefix, organizationId, {
        onlyEditAccess,
        type,
        types,
        startDate,
        endDate,
        query,
      }),
    {
      enabled: !!folderPrefix && !!organizationId && folderPrefix !== '/',
    }
  );

  const { folders, currentFolder } = useMemo(() => {
    const folders = data?.folders || [];
    const currentFolder = data?.current || null;
    return {
      folders,
      currentFolder,
    };
  }, [data]);

  return {
    folders,
    currentFolder,
    isLoading,
    error,
    isFetching,
  };
}

async function getFolderAssets(id, params) {
  const res = await get(`/folders/${id}/assets`, { params });
  return res?.data?.assets;
}

export function useFolderAssetsQuery(
  folderId,
  {
    type,
    types,
    startDate,
    endDate,
    query,
    enabled = true,
    includeTranscriptSnapshot = 0,
  } = {}
) {
  const { data, isLoading, error } = useQuery(
    [
      'folderAssets',
      folderId,
      type,
      (types || []).join(','),
      startDate,
      endDate,
      query,
      includeTranscriptSnapshot,
    ],
    () =>
      getFolderAssets(folderId, {
        type,
        types: (types || []).join(','),
        startDate,
        endDate,
        query,
        includeTranscriptSnapshot,
      }),
    {
      enabled: !!enabled,
      refetchOnWindowFocus: UPLOADING_COUNTER === 0,
      refetchOnReconnect: UPLOADING_COUNTER === 0,
      refetchOnMount: UPLOADING_COUNTER === 0,
    }
  );

  return {
    assets: data,
    isLoading,
    error,
  };
}

async function getSnapshot(assetId) {
  const res = await get(`/files/${assetId}/snapshot`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  return url;
}

export async function getPoster(
  id,
  { width, height, fit, isGuest = false } = {}
) {
  try {
    const res = await get(`${isGuest ? '/guest' : ''}/meeting/${id}/poster`, {
      params: { width, height, fit },
      responseType: 'blob',
    });

    if (!res.data.size) return null;

    const url = window.URL.createObjectURL(new Blob([res?.data]));
    return url;
  } catch (err) {
    return null;
  }
}

async function getAssetImage(assetId, isFile, width = 232) {
  if (isFile) {
    return getSnapshot(assetId, {
      width,
    });
  } else {
    return getPoster(assetId, {
      width,
      height: 150,
      fit: 'contain',
    });
  }
}

export function useAssetImageQuery(assetId, isFile, { enabled, width }) {
  const key = ['assetImage', assetId, width];
  const { data, isLoading, error } = useQuery(
    key,
    () => getAssetImage(assetId, isFile, width),
    {
      enabled,
      cacheTime: 'Infinity',
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchInterval: false,
      refetchIntervalInBackground: false,
      retry: false,
    }
  );

  return {
    image: data,
    isLoading,
    error,
    cacheKey: key,
  };
}

async function getAssetTree() {
  const res = await get('/asset-tree');
  return res?.data;
}

export function useAssetTree() {
  const { data: assetTree } = useQuery('assetTree', getAssetTree);
  return {
    assetTree,
  };
}
